import { useState, useRef, useEffect } from 'react';
import { ChevronsUpDown, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../hooks/useAuth';

/**
 * Dropdown to switch between spaces (agency mode).
 * Only renders when the user has multiple spaces.
 * Designed for placement in the dashboard header.
 */
export function SpaceSwitcher() {
  const { availableSpaces, currentSpace, switchSpace } = useAuth();
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // V4-FRONT-SPACESWITCHER-CONDITIONAL-HOOK 2026-06-03 — ce useEffect DOIT rester avant
  // tout early-return : un hook appelé après `if (...) return null` viole les rules-of-hooks
  // (nombre de hooks variable entre renders → React #310 "rendered fewer hooks than expected").
  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  if (availableSpaces.length <= 1) return null;

  const handleSwitch = (spaceId: string) => {
    switchSpace(spaceId);
    setIsOpen(false);
    window.location.reload();
  };

  return (
    // V4-ONBOARDING-TOUR 2026-06-18 — cible du tour produit (parcours agence).
    <div className="relative" ref={dropdownRef} data-tour="space-switcher">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="group flex items-center justify-center w-9 h-9 rounded-full transition-colors"
        style={{
          backgroundColor: isOpen ? 'var(--color-primary)' : 'transparent',
          border: `1px solid var(--color-border)`,
        }}
        onMouseEnter={(e) => {
          if (!isOpen) e.currentTarget.style.backgroundColor = 'var(--color-primary)';
          const icon = e.currentTarget.querySelector('svg');
          if (icon) (icon as unknown as HTMLElement).style.color = 'var(--color-text-light)';
        }}
        onMouseLeave={(e) => {
          if (!isOpen) e.currentTarget.style.backgroundColor = 'transparent';
          const icon = e.currentTarget.querySelector('svg');
          if (icon) (icon as unknown as HTMLElement).style.color = isOpen ? 'var(--color-text-light)' : 'var(--color-text-primary)';
        }}
        title={currentSpace?.name || t.sidebar.switchSpace}
      >
        {currentSpace?.logoUrl ? (
          <img src={currentSpace.logoUrl} alt={currentSpace.name} className="w-5 h-5 rounded-full object-cover" />
        ) : (
          <ChevronsUpDown
            className="w-4 h-4 flex-shrink-0 transition-colors"
            style={{ color: isOpen ? 'var(--color-text-light)' : 'var(--color-text-primary)' }}
          />
        )}
      </button>

      {/* Dropdown — opens below, aligned to the right */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 z-50 min-w-[200px] rounded-xl shadow-2xl overflow-hidden"
            style={{
              backgroundColor: 'var(--color-background)',
              border: '1px solid var(--color-border)',
              // Fix V4-UI-SWITCHER-OVERFLOW (2026-05-21) : empêche le dropdown
              // de déborder à droite du viewport quand le bouton est près du
              // bord. Comme on est ancré `right-0`, la croissance se fait
              // naturellement vers la gauche jusqu'à cette limite.
              maxWidth: 'calc(100vw - 1rem)',
            }}
          >
            <div
              className="px-3 py-2 text-xs font-semibold uppercase tracking-wider"
              style={{ color: 'var(--color-text-secondary)', borderBottom: '1px solid var(--color-border)' }}
            >
              {t.sidebar.switchSpace}
            </div>
            <div className="max-h-[240px] overflow-y-auto py-1">
              {availableSpaces.map((space) => (
                <button
                  key={space.id}
                  onClick={() => handleSwitch(space.id)}
                  className="w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors"
                  style={{
                    backgroundColor: space.id === currentSpace?.id ? 'rgba(85,0,255,0.08)' : 'transparent',
                    color: 'var(--color-text-primary)',
                  }}
                  onMouseEnter={(e) => {
                    if (space.id !== currentSpace?.id) e.currentTarget.style.backgroundColor = 'rgba(85,0,255,0.04)';
                  }}
                  onMouseLeave={(e) => {
                    if (space.id !== currentSpace?.id) e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {space.logoUrl ? (
                    <img src={space.logoUrl} alt={space.name} className="w-5 h-5 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0"
                      style={{ backgroundColor: 'var(--color-primary)', color: '#fff' }}
                    >
                      {space.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="flex-1 text-left truncate">{space.name}</span>
                  {space.id === currentSpace?.id && (
                    <Check className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-primary)' }} />
                  )}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
